import type { CrmData } from "../../types";
import { arSummary, attentionItems, companiesByStatus, computeKpis, ownerLeaderboard, pipelineByStage } from "../../lib/analytics";
import { money } from "../../lib/format";
import { Avatar } from "../ui/Avatar";

const STAGE_COLOR: Record<string, string> = {
  "New Lead": "var(--dot-slate)",
  Qualified: "var(--dot-blue)",
  "Proposal Sent": "var(--dot-violet)",
  Negotiation: "var(--dot-amber)",
};

const STATUS_COLOR: Record<string, string> = {
  Prospect: "var(--dot-blue)",
  "Active Customer": "var(--dot-green)",
  "At Risk": "var(--dot-amber)",
  Churned: "var(--dot-slate)",
};

function Donut({ percent }: { percent: number }) {
  const r = 52;
  const c = 2 * Math.PI * r;
  const filled = (percent / 100) * c;
  return (
    <svg viewBox="0 0 130 130" class="donut" role="img" aria-label={`Win rate ${percent}%`}>
      <circle cx="65" cy="65" r={r} fill="none" stroke="var(--surface-inset)" stroke-width="14" />
      <circle
        cx="65"
        cy="65"
        r={r}
        fill="none"
        stroke="var(--accent)"
        stroke-width="14"
        stroke-linecap="round"
        stroke-dasharray={`${filled} ${c - filled}`}
        transform="rotate(-90 65 65)"
      />
      <text x="65" y="61" text-anchor="middle" class="donut__value">
        {percent}%
      </text>
      <text x="65" y="80" text-anchor="middle" class="donut__label">
        win rate
      </text>
    </svg>
  );
}

export function DashboardPage({
  data,
  now,
  onOpenOpportunity,
  onOpenInvoice,
}: {
  data: CrmData;
  now: Date;
  onOpenOpportunity: (id: string) => void;
  onOpenInvoice: (id: string) => void;
}) {
  const kpis = computeKpis(data);
  const stages = pipelineByStage(data);
  const maxStageValue = Math.max(1, ...stages.map((s) => s.value));
  const owners = ownerLeaderboard(data);
  const maxOwnerValue = Math.max(1, ...owners.map((o) => o.wonValue + o.openValue));
  const ar = arSummary(data, now);
  const statuses = companiesByStatus(data);
  const attention = attentionItems(data, now).slice(0, 6);

  const arTotal = Math.max(1, ar.collected + ar.outstanding);
  const collectedPct = (ar.collected / arTotal) * 100;
  const overduePct = (ar.overdue / arTotal) * 100;
  const outstandingPct = Math.max(0, ((ar.outstanding - ar.overdue) / arTotal) * 100);

  return (
    <div class="page">
      <div class="page__header">
        <div>
          <h1 class="page__title">Overview</h1>
          <p class="page__subtitle">Your sales and revenue at a glance</p>
        </div>
      </div>

      {/* KPI strip */}
      <div class="kpi-strip">
        <div class="kpi-tile">
          <div class="kpi-tile__label">Open pipeline</div>
          <div class="kpi-tile__value">{money(kpis.openPipeline)}</div>
          <div class="kpi-tile__meta">{money(kpis.weightedForecast)} weighted</div>
        </div>
        <div class="kpi-tile">
          <div class="kpi-tile__label">Won revenue</div>
          <div class="kpi-tile__value">{money(kpis.wonRevenue)}</div>
          <div class="kpi-tile__meta">{kpis.wonCount} deals closed</div>
        </div>
        <div class="kpi-tile">
          <div class="kpi-tile__label">Avg. deal size</div>
          <div class="kpi-tile__value">{money(kpis.avgDealSize)}</div>
          <div class="kpi-tile__meta">across won deals</div>
        </div>
        <div class="kpi-tile">
          <div class="kpi-tile__label">Outstanding AR</div>
          <div class="kpi-tile__value">{money(ar.outstanding)}</div>
          <div class="kpi-tile__meta">{ar.overdue > 0 ? `${money(ar.overdue)} overdue` : "nothing overdue"}</div>
        </div>
      </div>

      <div class="dash-grid">
        {/* Pipeline by stage */}
        <section class="panel dash-grid__wide">
          <div class="panel__head">
            <h3>Pipeline by stage</h3>
            <span class="panel__hint">{money(kpis.openPipeline)} open</span>
          </div>
          <div class="bars">
            {stages.map((s) => (
              <div class="bar-row" key={s.stage}>
                <span class="bar-row__label">
                  <span class="bar-row__dot" style={{ background: STAGE_COLOR[s.stage] }} />
                  {s.stage}
                </span>
                <span class="bar-row__track">
                  <span class="bar-row__fill" style={{ width: `${(s.value / maxStageValue) * 100}%`, background: STAGE_COLOR[s.stage] }} />
                </span>
                <span class="bar-row__value">
                  {money(s.value)} <span class="bar-row__count">· {s.count}</span>
                </span>
              </div>
            ))}
          </div>
        </section>

        {/* Win rate */}
        <section class="panel">
          <div class="panel__head">
            <h3>Win rate</h3>
          </div>
          <div class="donut-wrap">
            <Donut percent={kpis.winRate} />
          </div>
          <div class="donut-legend">
            <span>
              <span class="dot" style={{ background: "var(--dot-green)" }} /> {kpis.wonCount} won
            </span>
            <span>
              <span class="dot" style={{ background: "var(--dot-red)" }} /> {kpis.lostCount} lost
            </span>
          </div>
        </section>

        {/* Leaderboard */}
        <section class="panel dash-grid__wide">
          <div class="panel__head">
            <h3>Top performers</h3>
            <span class="panel__hint">by won revenue</span>
          </div>
          <div class="leaderboard">
            {owners.map((o) => (
              <div class="lb-row" key={o.owner}>
                <Avatar name={o.owner} size="sm" />
                <div class="lb-row__main">
                  <div class="lb-row__top">
                    <span class="lb-row__name">{o.owner}</span>
                    <span class="lb-row__value">{money(o.wonValue)}</span>
                  </div>
                  <span class="lb-row__track">
                    <span class="lb-row__fill lb-row__fill--won" style={{ width: `${(o.wonValue / maxOwnerValue) * 100}%` }} />
                    <span class="lb-row__fill lb-row__fill--open" style={{ width: `${(o.openValue / maxOwnerValue) * 100}%` }} />
                  </span>
                  <span class="lb-row__meta">
                    {o.wonCount} won · {money(o.openValue)} open pipeline
                  </span>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Accounts receivable + accounts by status */}
        <section class="panel">
          <div class="panel__head">
            <h3>Accounts receivable</h3>
          </div>
          <div class="ar-figure">{money(ar.collected)}</div>
          <div class="ar-sub">collected of {money(ar.invoiced)} invoiced</div>
          <div class="seg-bar">
            <span class="seg-bar__part" style={{ width: `${collectedPct}%`, background: "var(--dot-green)" }} />
            <span class="seg-bar__part" style={{ width: `${outstandingPct}%`, background: "var(--dot-blue)" }} />
            <span class="seg-bar__part" style={{ width: `${overduePct}%`, background: "var(--dot-red)" }} />
          </div>
          <div class="seg-legend">
            <span>
              <span class="dot" style={{ background: "var(--dot-green)" }} /> Collected {money(ar.collected)}
            </span>
            <span>
              <span class="dot" style={{ background: "var(--dot-blue)" }} /> Outstanding {money(ar.outstanding - ar.overdue)}
            </span>
            <span>
              <span class="dot" style={{ background: "var(--dot-red)" }} /> Overdue {money(ar.overdue)}
            </span>
          </div>
          <div class="status-chips">
            {statuses.map((s) => (
              <span class="status-chip" key={s.status}>
                <span class="dot" style={{ background: STATUS_COLOR[s.status] }} />
                {s.status}
                <strong>{s.count}</strong>
              </span>
            ))}
          </div>
        </section>

        {/* Needs attention */}
        <section class="panel dash-grid__full">
          <div class="panel__head">
            <h3>Needs attention</h3>
            <span class="panel__hint">{attention.length} items</span>
          </div>
          {attention.length === 0 ? (
            <p class="empty-inline">Everything is on track. Nothing needs attention right now.</p>
          ) : (
            <div class="attention-list">
              {attention.map((item) => (
                <button
                  type="button"
                  key={`${item.kind}-${item.id}`}
                  class="attention-item"
                  data-ai-id={`${item.kind === "invoice" ? "open-invoice" : "open-opportunity"}-${item.id}`}
                  onClick={() => (item.kind === "invoice" ? onOpenInvoice(item.id) : onOpenOpportunity(item.id))}
                >
                  <span class={`attention-item__dot attention-item__dot--${item.severity}`} />
                  <span class="attention-item__body">
                    <span class="attention-item__title">{item.title}</span>
                    <span class="attention-item__reason">{item.reason}</span>
                  </span>
                  <span class="attention-item__amount">{money(item.amount)}</span>
                </button>
              ))}
            </div>
          )}
        </section>
      </div>
    </div>
  );
}
